import { EnvCtx } from "@mirage/service-environment-context/global-env-ctx";
import { tagged } from "@mirage/service-logging";
import { isStagingBuildChannel } from "@mirage/shared/util/tiny-utils";
import { useCallback, useEffect, useState } from "react";
import { listenForApiEnvUpdates, setApiEnvironment } from "./";
import { ApiEnvironment } from "./types";

const logger = tagged("useApiEnvironment");

/**
 * Returns the default API environment for the current execution context.
 */
export function defaultApiEnvForThisEnv(): ApiEnvironment {
  const isStaging =
    EnvCtx.environment === "development" ||
    isStagingBuildChannel(EnvCtx.buildChannel);
  return isStaging ? ApiEnvironment.STAGE : ApiEnvironment.PROD;
}

export function useApiEnvironment() {
  const [environment, setEnvironment] = useState<ApiEnvironment | null>(null); // Initialize as null
  const defaultEnv = defaultApiEnvForThisEnv();

  logger.debug("useApiEnvironment initializing with defaultEnv:", defaultEnv);

  useEffect(() => {
    const subscription = listenForApiEnvUpdates().subscribe({
      next: (env) => {
        logger.debug("useApiEnvironment received new environment:", env);
        if (env !== environment) {
          setEnvironment(env);
        }
      },
      error: (error) => {
        logger.error("Failed to subscribe to API environment changes", error);
      },
    });

    return () => {
      subscription.unsubscribe();
    };
  }, [defaultEnv, environment]);

  const onChange = useCallback((value: ApiEnvironment | null) => {
    setApiEnvironment(value).catch((error) => {
      logger.error("Failed to set manual API environment override", error);
    });
  }, []);

  return [environment, onChange] as const;
}
